import { push } from 'connected-react-router';
import { all, call, cancel, fork, put, select, spawn, take } from 'redux-saga/effects';
import { VISIBILITY_CHANGE } from '../../modules/app/actions';
import { visibilityChangeChannel } from '../../modules/app/channels';
import { getPathname } from '../../modules/app/selectors';
import { loadEvents } from '../../modules/events/saga';
import { hasEvents, isEventExists } from '../../modules/events/selectors';
import watchInPlayChanges from '../../modules/livescore';
import watchPDSChangesV6 from '../../modules/pdsV6';
import { EVENT_IN_PLAY_CHANGE } from '../../modules/pds/actions';
import { LIVE_SCORES } from '../../routes/paths';
import {
  DETAILS_VIEW_ENTER,
  DETAILS_VIEW_LEAVE,
  detailsViewLeave,
  SET_EVENTS_DAY,
  setEventsDay,
  START_LIVE_EVENTS,
  startLiveEvents,
  VIEW_ENTER,
  VIEW_LEAVE,
  viewEnter,
  viewLeave
} from './actions';
import { getEventsDay, getLiveEvents, getPDSTopicParams } from './selectors';

export function* watchEventsViewEnter() {
  while (yield take([VIEW_ENTER, SET_EVENTS_DAY])) {
    const day = yield select(getEventsDay);
    if (yield call(loadEvents, day, true)) {
      yield put(startLiveEvents());
    }
  }
}

export function* watchEventDetailsViewEnter() {
  while (true) {
    const { day, eventId } = yield take(DETAILS_VIEW_ENTER);
    const allowedDays = [0];

    if (!eventId.match(/^OB_EV[\d]+$/) || !allowedDays.includes(day)) {
      yield put(push(LIVE_SCORES));
      return;
    }

    const loadedDay = yield select(getEventsDay);
    let loadedEvents = yield select(hasEvents);
    if (!loadedEvents || day !== loadedDay) {
      loadedEvents = yield call(loadEvents, day, true);
    }

    const isEventFound = yield select(isEventExists, { eventId });
    if (!loadedEvents || !isEventFound) {
      yield put(setEventsDay(day));
      yield put(push(LIVE_SCORES));
    } else {
      yield put(startLiveEvents());
    }
  }
}

export function* watchEventsChanges() {
  let pdsTask;
  let inPlayTask;
  const takeActions = [SET_EVENTS_DAY, START_LIVE_EVENTS, VIEW_LEAVE, DETAILS_VIEW_LEAVE];
  const actionsWithLiveChannels = [START_LIVE_EVENTS];
  while (true) {
    const { type } = yield take(takeActions);
    if (pdsTask) {
      yield cancel(pdsTask);
      pdsTask = null;
    }
    if (inPlayTask) {
      yield cancel(inPlayTask);
      inPlayTask = null;
    }
    if (actionsWithLiveChannels.includes(type)) {
      const watchedPDSActions = [EVENT_IN_PLAY_CHANGE];
      pdsTask = yield fork(watchPDSChangesV6, getPDSTopicParams, watchedPDSActions);
      inPlayTask = yield fork(watchInPlayChanges, getLiveEvents);
    }
  }
}

export function* watchWindowVisibility() {
  const channel = yield call(visibilityChangeChannel);
  while (true) {
    const { type, hidden } = yield take(channel);
    const path = yield select(getPathname);
    if (path === LIVE_SCORES && type === VISIBILITY_CHANGE) {
      yield put((hidden) ? viewLeave() : viewEnter());
    }
    if (path.startsWith(`${LIVE_SCORES}/`) && type === VISIBILITY_CHANGE) {
      yield put((hidden) ? detailsViewLeave() : startLiveEvents());
    }
  }
}

export default function* root() {
  yield all([
    spawn(watchEventsViewEnter),
    spawn(watchEventDetailsViewEnter),
    spawn(watchEventsChanges),
    spawn(watchWindowVisibility)
  ]);
}
/* eslint-enable no-constant-condition */
